import {BuyBonusButtonConstants} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/BuyBonusButtonConstants';
import {KeypadView} from '@gongxigames/framework/slot/modules/keypad/KeypadView';
import {Container, Sprite, Text, Texture} from 'pixi.js';
import {
    AnimationProperty,
    CoreDI,
    CurrencyFormatter,
    DeviceType,
    IAdvancedTextConfig, inject,
    LanguageService,
    ModuleIsEnabledFilter, MovieClip, Spine
} from '@gongxigames/framework/core';
import {isEnabledRubyCoins} from '@gongxigames/framework/slot/modules/buyBonus/isEnabledRubyCoins';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export enum RubyBalanceState {
    Enabled = 1,
    Disabled,
}

export class BuyBonusBalanceView extends View {
    public static readonly NAME: symbol = Symbol('BuyBonusBalanceView');
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledRubyCoins;
    public static layer = LAYERS.OVER_KEYPAD;
    public static platform: DeviceType = DeviceType.DESKTOP;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    protected _advancedTexts: IAdvancedTextConfig[] = [];
    protected _spineContainer!: Container;
    protected _spine!: Spine;
    protected _rubyBalanceTextForSpine!: Text;
    protected _balanceContainer!: Container;
    protected _rubyBalanceSprite!: Sprite;
    protected _rubyBalanceText!: Text;
    protected _balanceBGMC!: MovieClip;

    public init(): void {
        if (this._gameConfig.buyButtonTXT) {
            this._advancedTexts = this._gameConfig.buyButtonTXT;
        }

        super.init();

        this.initGraphicsElements();
    }

    public initGraphicsElements(): void {
        this.initRubyBalanceSprite();
        this.initControls();
        this.initSpine();
    }

    public initRubyBalanceSprite(): void {
        this._rubyBalanceSprite = this.mainSprite.getChildByName<Sprite>('rubyBalance');
    }

    protected initControls(): void {
        this._balanceContainer = new Container();
        this._rubyBalanceText = this._rubyBalanceSprite.getChildByName<Text>('rubyBalanceText');
        this._balanceBGMC = this._rubyBalanceSprite.getChildByName<MovieClip>('balanceBG');
        this._balanceBGMC.gotoAndStop(RubyBalanceState.Enabled);

        this.container.addChild(this._rubyBalanceSprite);
    }

    protected initSpine(): void {
        const animationContainer = this._rubyBalanceSprite.getChildByName<Container>('animationContainer');
        const rubyBalance = this.objectBuilder.buildDisplayObjectByName('spineAttachments',
            'rubyBalance') as Sprite;

        this._spineContainer = new Container();
        this._spine = this.objectBuilder.buildSpine(BuyBonusButtonConstants.SPINE_NAME);
        this._rubyBalanceTextForSpine = rubyBalance.getChildByName<Text>('rubyBalanceText');

        animationContainer.addChild(this._spine);

        this._spineContainer.visible = false;
        this._spineContainer.addChild(animationContainer);
        this._spine.state.clearTracks();
        this._spine.start();

        this.updateBalanceStyleForSpine();
        this.clearTexture('text_1');
        this.clearTexture('text_2');
        this.addSpriteToSlot(rubyBalance, 'text_3', false, false);
        this.updateManager.add(this._spine);
        this.container.addChild(this._spineContainer);
    }

    protected updateBalanceStyleForSpine(): void {
        this._rubyBalanceTextForSpine.style.fontWeight = 'bold';
        this._rubyBalanceTextForSpine.style.fontFamily = 'PF DinDisplay Pro';
    }

    protected addSpriteToSlot(sprite: Sprite, slotName: string, centerX = true, centerY = true): void {
        const slotSprite = this._spine.skeleton.findSlot(slotName).currentSprite;

        this.clearTexture(slotName);
        sprite.x = (centerX ? -sprite.getLocalBounds().width : 1) / 2;
        sprite.y = (centerY ? -sprite.getLocalBounds().height : 1) / 2;

        slotSprite.addChild(sprite);
    }

    protected clearTexture(slotName: string): void {
        const slotSprite = this._spine.skeleton.findSlot(slotName).currentSprite;

        slotSprite.texture = Texture.EMPTY;
        slotSprite.removeChildren();
    }

    protected playAnimation(): void {
        const animProperty: AnimationProperty = {
            animationName: BuyBonusButtonConstants.COIN_LAND_ANIMATION_NAME,
            trackIndex: 0,
            loop: false,
            listeners: {
                complete: () => {
                    this._spineContainer.visible = false;
                    this._rubyBalanceSprite.visible = true;
                }
            }
        };

        this._spineContainer.visible = true;
        this._spine.setAnimation(animProperty);

        this.container.alpha = 1;
    }

    public prepareAnimation(): void {
        this._spineContainer.visible = true;
        this._rubyBalanceSprite.visible = false;
        this.playAnimation();
    }

    public setDisabledBalanceState(): void {
        this._balanceBGMC.gotoAndStop(RubyBalanceState.Disabled);
    }

    public setEnabledBalanceState(): void {
        this._balanceBGMC.gotoAndStop(RubyBalanceState.Enabled);
    }

    public updateRealMoneyValues(value: number): void {
        this._rubyBalanceText.text = this._currencyFormatter.formatCents(value);
        this._rubyBalanceTextForSpine.text = this._currencyFormatter.formatCents(value);
    }

    public updateRubyValues(value: number): void {
        this._rubyBalanceText.text = String(value);
        this._rubyBalanceTextForSpine.text = String(value);
    }

    protected get mainSprite(): Sprite {
        return this._installer.getComponent<KeypadView>(KeypadView.NAME).mainSprite;
    }
}
